import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Search, Mail, Shield, UserPlus, KeyRound, Clock, CheckCircle, Trash2, UserX, RefreshCw } from 'lucide-react';
import { api } from '../../api/client';

const cardStyle: React.CSSProperties = {
    background: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-color)',
    overflow: 'hidden'
};

const headerStyle: React.CSSProperties = {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16
};

const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    background: 'var(--accent-primary)',
    color: 'white',
    transition: 'all 0.15s ease'
};

const actionButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 8px',
    borderRadius: 4,
    border: 'none',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 500,
    background: 'var(--bg-tertiary)',
    color: 'var(--text-muted)',
    transition: 'all 0.15s ease'
};

export function UserManagement() {
    const { allUsers, roles, settings, updateUser, currentUser, departments } = useApp();

    // Helper to toggle departments
    const handleToggleDepartment = (user: any, deptId: string) => {
        const currentIds = user.department_ids || (user.department_id ? [user.department_id] : []);
        let newIds;
        if (currentIds.includes(deptId)) {
            newIds = currentIds.filter((id: string) => id !== deptId);
        } else {
            newIds = [...currentIds, deptId];
        }
        updateUser(user.id, { department_ids: newIds } as any);
    };

    const [searchTerm, setSearchTerm] = useState('');
    const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteForm, setInviteForm] = useState({ name: '', email: '', role_id: settings?.default_role_id || '', department_id: '' });
    const [inviting, setInviting] = useState(false);

    const handleRoleChange = async (userId: string, newRoleId: string) => {
        setLoadingMap(prev => ({ ...prev, [userId]: true }));
        try {
            await updateUser(userId, { role_id: newRoleId });
        } catch (error) {
            alert('Failed to update user role');
        } finally {
            setLoadingMap(prev => ({ ...prev, [userId]: false }));
        }
    };

    const handleInviteUser = async () => {
        if (!inviteForm.name || !inviteForm.email) {
            alert('Name and email are required');
            return;
        }
        setInviting(true);
        try {
            const result = await api.inviteUser(inviteForm);
            alert(`User invited! Share this link:\n${result.invite_link}`);
            setShowInviteModal(false);
            setInviteForm({ name: '', email: '', role_id: '', department_id: '' });
            // Note: Page refresh needed to see new user
        } catch (err: any) {
            alert(err.response?.data?.error || 'Failed to invite user');
        } finally {
            setInviting(false);
        }
    };

    const handleResetPassword = async (userId: string, email: string) => {
        if (!confirm(`Send password reset to ${email}?`)) return;
        setLoadingMap(prev => ({ ...prev, [`reset-${userId}`]: true }));
        try {
            const result = await api.adminResetPassword(userId);
            alert(`Password reset link: /reset-password?reset_token=${result.reset_token}`);
        } catch (err: any) {
            alert(err.response?.data?.error || 'Failed to reset password');
        } finally {
            setLoadingMap(prev => ({ ...prev, [`reset-${userId}`]: false }));
        }
    };

    const handleDeleteUser = async (userId: string, name: string) => {
        if (userId === currentUser?.id) {
            alert('You cannot delete your own account.');
            return;
        }
        if (!confirm(`Delete user "${name}"? Their message history will be preserved, but they will lose access immediately.`)) return;
        setLoadingMap(prev => ({ ...prev, [`delete-${userId}`]: true }));
        try {
            await api.deleteUser(userId);
            // The socket event / context update will reflect the status change.
            // Force a local state update by triggering updateUser read (no-op update).
        } catch (err: any) {
            alert(err.response?.data?.error || 'Failed to delete user');
        } finally {
            setLoadingMap(prev => ({ ...prev, [`delete-${userId}`]: false }));
        }
    };

    const handleReinviteUser = async (userId: string, name: string, email: string) => {
        if (!confirm(`Reinvite "${name}" (${email})? They will receive a new invite link and must set up their account again.`)) return;
        setLoadingMap(prev => ({ ...prev, [`reinvite-${userId}`]: true }));
        try {
            const result = await api.reinviteUser(userId);
            alert(`Reinvite sent! Share this link if email fails:\n${result.invite_link || `Check server logs for invite token`}`);
        } catch (err: any) {
            alert(err.response?.data?.error || 'Failed to reinvite user');
        } finally {
            setLoadingMap(prev => ({ ...prev, [`reinvite-${userId}`]: false }));
        }
    };

    const filteredUsers = allUsers.filter((u) => {
        const term = searchTerm.toLowerCase();
        return (
            u.name.toLowerCase().includes(term) ||
            u.email.toLowerCase().includes(term)
        );
    });

    return (
        <div style={cardStyle}>
            <div style={headerStyle}>
                <div>
                    <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>User Management</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Assign roles, invite users, and manage access.</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            className="input"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ paddingLeft: 36, width: 220 }}
                        />
                    </div>
                    <button style={buttonStyle} onClick={() => setShowInviteModal(true)}>
                        <UserPlus size={16} />
                        Invite User
                    </button>
                </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ background: 'var(--bg-tertiary)', fontSize: 11, textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                        <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600 }}>User</th>
                        <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600 }}>Invite Status</th>
                        <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600 }}>Department</th>
                        <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600 }}>Role</th>
                        <th style={{ padding: '12px 20px', textAlign: 'left', fontWeight: 600 }}>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredUsers.map((user, i) => {
                        const isDeleted = user.status === 'deleted';
                        const isPending = user.invite_status === 'pending' || user.invite_status !== 'accepted';
                        return (
                            <tr key={user.id} style={{ borderBottom: i < filteredUsers.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                                <td style={{ padding: '14px 20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div className="avatar" style={{ width: 36, height: 36, fontSize: 14 }}>
                                            {user.name.charAt(0)}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{user.name}</div>
                                            <div style={{ color: 'var(--text-muted)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <Mail size={12} /> {user.email}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td style={{ padding: '14px 20px' }}>
                                    {isDeleted ? (
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            padding: '4px 10px',
                                            borderRadius: 12,
                                            fontSize: 11,
                                            fontWeight: 600,
                                            background: 'rgba(239,68,68,0.1)',
                                            color: '#ef4444'
                                        }}>
                                            <UserX size={12} /> Deleted
                                        </span>
                                    ) : (
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            padding: '4px 10px',
                                            borderRadius: 12,
                                            fontSize: 11,
                                            fontWeight: 600,
                                            background: user.invite_status === 'accepted' ? '#d1fae5' : '#fef3c7',
                                            color: user.invite_status === 'accepted' ? '#059669' : '#d97706'
                                        }}>
                                            {user.invite_status === 'accepted' ? <CheckCircle size={12} /> : <Clock size={12} />}
                                            {user.invite_status === 'accepted' ? 'Active' : 'Pending'}
                                        </span>
                                    )}
                                </td>
                                <td style={{ padding: '14px 20px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
                                        {departments.length === 0 ? (
                                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>No departments</span>
                                        ) : (
                                            departments.map(dept => {
                                                const currentIds = user.department_ids || (user.department_id ? [user.department_id] : []);
                                                const isSelected = currentIds.includes(dept.id);
                                                return (
                                                    <label key={dept.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: isDeleted ? 'default' : 'pointer', opacity: isDeleted ? 0.5 : 1 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            disabled={isDeleted}
                                                            onChange={() => handleToggleDepartment(user, dept.id)}
                                                            style={{ margin: 0 }}
                                                        />
                                                        {dept.name}
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>
                                </td>
                                <td style={{ padding: '14px 20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: isDeleted ? 0.4 : 1 }}>
                                        <Shield size={16} color={user.role_id?.includes('admin') ? 'var(--accent-primary)' : 'var(--text-muted)'} />
                                        <select
                                            className="input select"
                                            value={user.role_id || ''}
                                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                            disabled={loadingMap[user.id] || isDeleted}
                                            style={{ padding: '6px 10px', fontSize: 13, minWidth: 140 }}
                                        >
                                            <option value="">No Role</option>
                                            {roles.map((role) => (
                                                <option key={role.id} value={role.id}>{role.name}</option>
                                            ))}
                                        </select>
                                        {loadingMap[user.id] && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Saving...</span>}
                                    </div>
                                </td>
                                <td style={{ padding: '14px 20px' }}>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {isPending && !isDeleted && (
                                            <button
                                                style={{ ...actionButtonStyle, color: '#4A9EFF' }}
                                                onClick={() => handleReinviteUser(user.id, user.name, user.email)}
                                                disabled={loadingMap[`reinvite-${user.id}`]}
                                                title="Resend invitation link"
                                            >
                                                <RefreshCw size={12} />
                                                {loadingMap[`reinvite-${user.id}`] ? '...' : 'Reinvite'}
                                            </button>
                                        )}
                                        {isDeleted ? (
                                            <button
                                                style={{ ...actionButtonStyle, color: '#4A9EFF' }}
                                                onClick={() => handleReinviteUser(user.id, user.name, user.email)}
                                                disabled={loadingMap[`reinvite-${user.id}`]}
                                                title="Reinvite this user — they will set up a fresh account"
                                            >
                                                <RefreshCw size={12} />
                                                {loadingMap[`reinvite-${user.id}`] ? 'Reinviting...' : 'Reinvite'}
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    style={actionButtonStyle}
                                                    onClick={() => handleResetPassword(user.id, user.email)}
                                                    disabled={loadingMap[`reset-${user.id}`]}
                                                >
                                                    <KeyRound size={12} />
                                                    {loadingMap[`reset-${user.id}`] ? 'Sending...' : 'Reset Password'}
                                                </button>
                                                {user.id !== currentUser?.id && (
                                                    <button
                                                        style={{ ...actionButtonStyle, color: '#ef4444' }}
                                                        onClick={() => handleDeleteUser(user.id, user.name)}
                                                        disabled={loadingMap[`delete-${user.id}`]}
                                                        title="Delete user (preserves message history)"
                                                    >
                                                        <Trash2 size={12} />
                                                        {loadingMap[`delete-${user.id}`] ? 'Deleting...' : 'Delete'}
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Invite User Modal */}
            {showInviteModal && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'var(--bg-secondary)',
                        borderRadius: 12,
                        padding: 24,
                        width: 400,
                        maxWidth: '90vw'
                    }}>
                        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Invite New User</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <input
                                type="text"
                                className="input"
                                placeholder="Full Name"
                                value={inviteForm.name}
                                onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))}
                            />
                            <input
                                type="email"
                                className="input"
                                placeholder="Email Address"
                                value={inviteForm.email}
                                onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                            />
                            <select
                                className="input select"
                                value={inviteForm.role_id}
                                onChange={e => setInviteForm(f => ({ ...f, role_id: e.target.value }))}
                            >
                                <option value="">Select Role</option>
                                {roles.map(r => <option key={r.id} value={r.id}>{r.name}{settings?.default_role_id === r.id ? ' (Default)' : ''}</option>)}
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
                            <button
                                style={{ ...buttonStyle, background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
                                onClick={() => setShowInviteModal(false)}
                            >
                                Cancel
                            </button>
                            <button style={buttonStyle} onClick={handleInviteUser} disabled={inviting}>
                                {inviting ? 'Inviting...' : 'Send Invite'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
